import {
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material'
import { GreenText, SortableTableCell } from 'components'
import * as controllers from 'controllers'
import { VehicleDto } from 'dtos'
import { VehicleTransactionDto } from 'dtos/invoicing'
import { DEBOUNCE_DELAY } from 'localConstants'
import { VehicleTransactionParameters } from 'parameters/vehicleTransactionParameters'
import { useEffect, useState } from 'react'
import { formatDateTime, useDebounce } from 'utils'

interface TransactionsDetailsCardProps {
  vehicle: VehicleDto
}

export default function TransactionsDetailsCard({ vehicle }: TransactionsDetailsCardProps) {
  const [isGettingTransactions, setIsGettingTransactions] = useState<boolean>(false)
  const [transactions, setTransactions] = useState<VehicleTransactionDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)

  const [parameters, setParameters] = useState<VehicleTransactionParameters>({
    page: 0,
    pageSize: 10,
    sortBy: 'transactionDateTime',
    sortDirection: 'desc'
  })

  const onChangeOrderBy = (orderBy: string) => {
    setParameters(prev => ({
      ...parameters,
      orderBy,
      order: prev.orderBy === orderBy && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }

  useEffect(() => {
    if (vehicle.vehicleGuid) {
      getVehicleTransactions(vehicle.vehicleGuid, parameters)
    }
  }, [vehicle.vehicleGuid, parameters])

  const getVehicleTransactions = useDebounce((vehicleGuid: string, parameters: VehicleTransactionParameters) => {
    setIsGettingTransactions(true)
    controllers
      .getVehicleTransactions(vehicleGuid, parameters)
      .then(response => {
        setTransactions(response.value)
        setTotalCount(response.totalCount!)
      })
      .finally(() => {
        setIsGettingTransactions(false)
      })
  }, DEBOUNCE_DELAY)

  return (
    <Card variant='outlined'>
      <CardContent>
        <Grid container spacing={1} alignItems='flex-start'>
          <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
            <Grid item xs={12} sm={8}>
              <Typography fontWeight='bold' variant='h5'>
                Transactions
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {isGettingTransactions && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingTransactions && (
              <Grid item xs={12}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <SortableTableCell
                          onChangeSortByDirection={onChangeOrderBy}
                          propertyName='createdDateTime'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Date/Time
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={onChangeOrderBy}
                          propertyName='chargeType'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Type
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={onChangeOrderBy}
                          propertyName='description'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Description
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={onChangeOrderBy}
                          propertyName='amount'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Amount
                        </SortableTableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {transactions.map(transaction => (
                        <TableRow>
                          <TableCell>{transaction.createdDateTime ? formatDateTime(transaction.createdDateTime) : ''}</TableCell>

                          <TableCell>{transaction.chargeType}</TableCell>

                          <TableCell>
                            {transaction.description.includes('Tracking #:')
                              ? (() => {
                                  const parts = transaction.description.split('Tracking #:')
                                  const trackingNumber = parts[1]?.trim() // everything after "Tracking #:"
                                  const trackingUrl = `https://www.fedex.com/fedextrack/?tracknumbers=${trackingNumber}`
                                  return (
                                    <>
                                      {parts[0]}
                                      {'Tracking #: '}
                                      <Link onClick={() => window.open(trackingUrl, '_blank')} sx={{ cursor: 'pointer' }}>
                                        {trackingNumber}
                                      </Link>
                                    </>
                                  )
                                })()
                              : transaction.description}
                          </TableCell>

                          <TableCell>{`$${transaction.amount.toFixed(2)}`}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>

                    <TableFooter>
                      <TableRow>
                        <TablePagination
                          count={totalCount}
                          onPageChange={(_, page) => {
                            setParameters({
                              ...parameters,
                              page
                            })
                          }}
                          onRowsPerPageChange={e => {
                            setParameters({
                              ...parameters,
                              page: 0,
                              pageSize: e.target.value as unknown as number
                            })
                          }}
                          page={parameters.page}
                          rowsPerPage={parameters.pageSize}
                          rowsPerPageOptions={[5, 10, 25]}
                          SelectProps={{ fullWidth: false }}
                        />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              </Grid>
            )}

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`When Vehicle Check-In Date field under “Vehicle Details – Vehicle Check-In (tab)” is populated, a transaction is added.

   - Date/Time: (current date/time),
   - Type: Import Charge,
   - Description: System Generated: Vehicle Check-In Completed,
   - Amount: Reads from the “Import Charge” field on the Rates card of the Buyer Company if one is associated with the vehicle, else the Exporter Company.

When Vehicle Check-In Date field under “Vehicle Details – Vehicle Check-In (tab)” is populated, a transaction is added.

   - Date/Time: (current date/time),
   - Type: Inbound Transportation,
   - Description: System Generated: Vehicle Check-In Completed,
   - Amount: Reads from the “Inbound Transportation” field on the Rates card of the Buyer Company if one is associated with the vehicle, else the Exporter Company.

When Cluster Conversion = Yes and “Cluster Reinstallation Complete” has a date on the cluster timeline, a transaction is added.

   - Date/Time: (current date/time),
   - Type: Cluster Conversion,
   - Description: System Generated: Cluster Reinstallation Completed,
   - Amount: Reads from the “Cluster Conversion” field on the Rates card of the Buyer Company if one is associated with the vehicle, else the Exporter Company.

When Additional Programming = Yes and “Cluster Reinstallation Complete” has a date on the cluster timeline, a transaction is added.

   - Date/Time: (current date/time),
   - Type: Cluster Programming,
   - Description: System Generated: Cluster Reinstallation Completed,
   - Amount: Reads from the “Cluster Programming” field on the Rates card of the Buyer Company if one is associated with the vehicle, else the Exporter Company.

When the first Recall Campaign is created where “Recall/No Recall” = “Recall”, a transaction is added.

   - Date/Time: (current date/time),
   - Type: Recall Expenses,
   - Description: System Generated: First Recall Campaign Created,
   - Amount: Reads from the “Recall Expenses” field on the Rates card of the Buyer Company if one is associated with the vehicle, else the Exporter Company.

When “Sent to DOT” field under “Vehicle Details – Conformity (tab)” is populated, a transaction is added.

   - Date/Time: (current date/time),
   - Type: Title Fees,
   - Description: System Generated: Conformity Sent to DOT,
   - Amount: Reads from the “Title Fees” field on the Rates card of the Buyer Company if one is associated with the vehicle, else the Exporter Company.

When the Signed BOL is OCR’d and associated to the Outbound Trip the vehicle is associated with, a transaction is added.

   - Date/Time: (current date/time),
   - Type: Outbound Transportation,
   - Description: System Generated: Signed Outbound BOL Uploaded,
   - Amount: Reads from the “Outbound Transportation” field on the Rates card of the Buyer Company if one is associated with the vehicle, else the Exporter Company.

When a document is uploaded where type = “FedEx”, a transaction is added.

   - Date/Time: (current date/time),
   - Type: FedEx,
   - Description: {as entered by user in the Description field when document was uploaded},
   - Amount: {as entered by user in the Amount Per VIN field when document was uploaded}.

When a document is uploaded where type = “Replacement Label”, a transaction is added.

   - Date/Time: (current date/time),
   - Type: Replacement Label,
   - Description: {as entered by user in the Description field when document was uploaded},
   - Amount: {as entered by user in the Amount field when document was uploaded}.

When a document is uploaded where type = “Repairs”, a transaction is added.

   - Date/Time: (current date/time),
   - Type: Repairs,
   - Description: {as entered by user in the Description field when document was uploaded},
   - Amount: {as entered by user in the Amount field when document was uploaded}.

When a document is uploaded where type = “Vehicle Clean Up”, a transaction is added.

   - Date/Time: (current date/time),
   - Type: Vehicle Clean Up,
   - Description: {as entered by user in the Description field when document was uploaded},
   - Amount: {as entered by user in the Amount field when document was uploaded}.

When a document is uploaded where type = “Miscellaneous”, a transaction is added.

   - Date/Time: (current date/time),
   - Type: Miscellaneous,
   - Description: {as entered by user in the Description field when document was uploaded},
   - Amount: {as entered by user in the Amount field when document was uploaded}.

“FedEx Tracking #” is a link that when pressed opens the FedEx tracking site for that Tracking #.

For “Build A Box (Ohio)”, after the shipments have been created within FedEx and Tracking #s are available, two transactions are added:

   - Date/Time: (current date/time),
   - Type: FedEx API,
   - Description: Build A Box (Ohio) - FedEx Outbound Shipment. Tracking #: {FedEx Tracking #},
   - Amount: {prorated amount of the Outbound package}.

   - Date/Time: (current date/time),
   - Type: FedEx API,
   - Description: Build A Box (Ohio) - FedEx Return Shipment. Tracking #: {FedEx Tracking #},
   - Amount: {prorated amount of the Return package}.

For “Build A Box (Ohio)”, if the box is “Unlocked”, two negative transactions are added to deduct the costs associated with the FedEx Outbound Shipment and FedEx Return Shipment:

   - Date/Time: (current date/time),
   - Type: FedEx API,
   - Description: Build A Box (Ohio) - FedEx Outbound Shipment Cancelation,
   - Amount: {the negative prorated amount of the Outbound package}.

   - Date/Time: (current date/time),
   - Type: FedEx API,
   - Description: Build A Box (Ohio) - FedEx Return Shipment Cancelation,
   - Amount: {the negative prorated amount of the Return package}.

For Michigan Outgoing Titles Groups and Ohio Outgoing Titles Groups, after the shipments have been created within FedEx and Tracking #s are available, a transaction is added:

   - Date/Time: (current date/time),
   - Type: FedEx API,
   - Description: Outgoing Titles Group FedEx Shipment. Tracking #: {Tracking #},
   - Amount: {prorated amount of the Outgoing Titles Group FedEx Shipment}.

For Michigan Outgoing Titles Groups and Ohio Outgoing Titles Groups, after the shipments have been created within FedEx and Tracking #s are available, if a user presses “Unlock Group” and confirms that they do wish to unlock the group in the warning dialog, a transaction is added:

   - Date/Time: (current date/time),
   - Type: FedEx API,
   - Description: Outgoing Titles Group FedEx Shipment Cancelation,
   - Amount: {the negative prorated amount of the Outgoing Titles Group FedEx Shipment}.`}
              </GreenText>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
